import { useEffect, useRef, useState } from 'react';

type ElementSize = {
  width: number | undefined;
  height: number | undefined;
};

/** Observe a mounted element without a React-specific resize adapter. */
export default function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<ElementSize>({ width: undefined, height: undefined });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = (width: number, height: number) => {
      if (width <= 0 || height <= 0) return;
      setSize(previous =>
        previous.width === width && previous.height === height
          ? previous
          : { width, height }
      );
    };
    const measure = () => {
      const { width, height } = element.getBoundingClientRect();
      update(width, height);
    };

    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(entries => {
      const entry = entries.find(item => item.target === element);
      if (entry) update(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ...size, ref };
}
